import { Notification } from "@/components/admin/notification"
import { TextInput } from "@/components/admin/text-input"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { supabaseClient } from "@/lib/utils/client.supabase.conf"
import { Form, required, useNotify } from "ra-core"
import { useEffect, useState } from "react"
import type { FieldValues, SubmitHandler } from "react-hook-form"
import { useNavigate } from "react-router-dom"

export const ResetPasswordPage = () => {
  const [loading, setLoading] = useState(false)
  const [validToken, setValidToken] = useState(false)
  const [checking, setChecking] = useState(true)
  const [isInvitation, setIsInvitation] = useState(false)
  const notify = useNotify()
  const navigate = useNavigate()

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        // First check if there's a hash fragment with access_token
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        )
        const accessToken = hashParams.get("access_token")
        const type = hashParams.get("type")

        if (
          accessToken &&
          (type === "recovery" || type === "invite" || type === "signup")
        ) {
          // We have a token from the email link (password reset or invitation)
          const { data, error } = await supabaseClient.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get("refresh_token") || "",
          })

          if (error) {
            throw error
          }

          if (data.session) {
            setValidToken(true)
            setIsInvitation(type === "invite" || type === "signup")
            setChecking(false)
            // Clean up the URL
            window.history.replaceState({}, "", "/reset-password")
            return
          }
        }

        // Fallback: check if we already have a valid session
        const {
          data: { session },
        } = await supabaseClient.auth.getSession()

        if (session) {
          setValidToken(true)
        } else {
          notify("Lien de réinitialisation invalide ou expiré", {
            type: "error",
          })
          setTimeout(() => navigate("/login"), 3000)
        }
      } catch (error: any) {
        console.error("Password reset error:", error)
        notify("Erreur lors de la vérification du lien", {
          type: "error",
        })
        setTimeout(() => navigate("/login"), 3000)
      } finally {
        setChecking(false)
      }
    }

    handlePasswordReset()
  }, [navigate, notify])

  const handleSubmit: SubmitHandler<FieldValues> = async values => {
    if (values.password !== values.confirmPassword) {
      notify("Les mots de passe ne correspondent pas", {
        type: "error",
      })
      return
    }

    if (values.password.length < 6) {
      notify("Le mot de passe doit contenir au moins 6 caractères", {
        type: "error",
      })
      return
    }

    setLoading(true)
    try {
      const { error } = await supabaseClient.auth.updateUser({
        password: values.password,
      })

      if (error) {
        throw error
      }

      notify("Mot de passe réinitialisé avec succès", {
        type: "success",
      })

      // For invitations, keep user logged in; for password reset, sign out
      if (!isInvitation) {
        await supabaseClient.auth.signOut()
      }

      setTimeout(() => navigate(isInvitation ? "/" : "/login"), 1500)
    } catch (error: any) {
      notify(
        error?.message ||
          "Une erreur s'est produite lors de la réinitialisation",
        {
          type: "error",
        }
      )
    } finally {
      setLoading(false)
    }
  }

  if (checking || !validToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">
          {checking ? "Vérification du lien..." : "Redirection..."}
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      <div className="container relative grid flex-col items-center justify-center sm:max-w-none lg:grid-cols-2 lg:px-0">
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div
            className="absolute inset-0 bg-zinc-900"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1471604571702-62f6c507f68c?q=80&w=2748&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative z-20 mt-auto">
            <blockquote className="space-y-2">
              <p className="text-lg"></p>
              <footer className="text-sm text-white">{`Förnya ©  ${new Date().getFullYear()}`}</footer>
            </blockquote>
          </div>
        </div>
        <div className="lg:p-8">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[40vw] xs:w-[80vw]">
            <div className="flex flex-col space-y-2 text-center items-center my-18 ">
              <Avatar className="w-40 h-40">
                <AvatarImage src="https://galvtfsffmozysmfvnnw.supabase.co/storage/v1/object/public/images/system/logo600x600.png" />
              </Avatar>
              <h1 className="text-2xl font-semibold tracking-tight">
                {isInvitation
                  ? "Créer votre mot de passe"
                  : "Créer un nouveau mot de passe"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isInvitation
                  ? "Bienvenue ! Créez votre mot de passe pour accéder à votre compte"
                  : "Entrez votre nouveau mot de passe ci-dessous"}
              </p>
            </div>
            <Form className="space-y-4" onSubmit={handleSubmit}>
              <TextInput
                label="Nouveau mot de passe"
                source="password"
                type="password"
                validate={required()}
              />
              <TextInput
                label="Confirmer le mot de passe"
                source="confirmPassword"
                type="password"
                validate={required()}
              />
              <Button
                type="submit"
                className="cursor-pointer w-full my-12"
                disabled={loading}
              >
                {loading
                  ? "Enregistrement..."
                  : isInvitation
                    ? "Créer mon compte"
                    : "Réinitialiser le mot de passe"}
              </Button>
              {!isInvitation && (
                <div className="text-center">
                  <a
                    href="/login"
                    className="text-sm underline-offset-4 hover:underline"
                  >
                    Retour à la connexion
                  </a>
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  )
}
